(() => {
  if (window.__cnHomeHomepageLoaded) return;
  window.__cnHomeHomepageLoaded = true;

  const controllers = new WeakMap();
  const themePreviewControllers = new WeakMap();

  class CardcomplexVideoHero {
    constructor(section) {
      this.section = section;
      this.panel = section.querySelector('.cn-home-video-hero');
      this.topLane = section.querySelector('[data-cn-home-lane="top"]');
      this.bottomLane = section.querySelector('[data-cn-home-lane="bottom"]');
      this.deckLine = section.querySelector('[data-cn-home-deck-line]');
      this.deckPhrase = section.querySelector('[data-cn-home-deck-phrase]');
      this.wordElement = section.querySelector('[data-cn-home-word]');

      if (!this.panel || !this.topLane || !this.bottomLane) return;

      this.storefrontEndpoint =
        section.dataset.cnHomeStorefrontEndpoint || '/api/2026-07/graphql.json';
      this.storefrontToken =
        section.dataset.cnHomeStorefrontToken || '';

      this.randomCardCount = Math.max(
        1,
        Math.floor(this.readNumber(section.dataset.cnHomeRandomCardCount, 50))
      );

      this.productCacheHours = Math.max(
        1,
        this.readNumber(section.dataset.cnHomeProductCacheHours, 6)
      );

      this.wordInterval = this.readNumber(
        section.dataset.cnHomeWordInterval,
        1400
      );

      this.desktopDuration = this.readNumber(
        section.dataset.cnHomeCardDurationDesktop,
        20
      );

      this.mobileDuration = this.readNumber(
        section.dataset.cnHomeCardDurationMobile,
        10
      );

      this.desktopGapPx = Math.max(
        0,
        this.readNumber(section.dataset.cnHomeCardGapDesktop, 30)
      );

      this.mobileGapPx = Math.max(
        0,
        this.readNumber(section.dataset.cnHomeCardGapMobile, 25)
      );

      this.mobileQuery = window.matchMedia
        ? window.matchMedia('(max-width: 749px)')
        : null;

      this.phraseQuery = window.matchMedia
        ? window.matchMedia('(max-width: 989px)')
        : null;

      this.cards = this.readFallbackCards();
      this.words = this.readWords();
      this.items = [];
      this.cardCursor = 0;

      this.panelWidth = 0;
      this.cardWidth = 95;
      this.spacing = 125;
      this.speed = 60;
      this.exitBuffer = 40;
      this.edgePeek = 28;
      this.duration = this.desktopDuration;
      this.gapPx = this.desktopGapPx;
      this.isMobileMode = false;
      this.isPhraseMode = false;

      this.rafId = null;
      this.lastFrame = 0;
      this.isDestroyed = false;
      this.isInViewport = true;
      this.isDocumentVisible = !document.hidden;

      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.resizeTimer = null;

      this.wordIntervalTimer = null;
      this.desktopWordTimer = null;
      this.phraseOutTimer = null;
      this.phraseInTimer = null;
      this.pendingWord = '';
      this.isWordAnimating = false;

      this.initialize();
    }

    async initialize() {
      /*
        Den ersten Storefront-Request so früh wie möglich starten.
        Während Layout, Wörter und Events initialisiert werden, läuft
        der Netzwerkrequest bereits parallel.
      */
      const firstPagePromise = this.storefrontToken
        ? this.fetchProductPage(null).catch((error) => {
            console.warn(
              '[Cardcomplex Hero] Erste Produktseite konnte nicht vorgeladen werden.',
              error
            );
            return null;
          })
        : Promise.resolve(null);

      this.measure();
      this.startWords();
      this.bindResize();
      this.bindLifecycle();

      const cachedProducts = this.readProductCache();

      if (cachedProducts.length) {
        this.cards = this.pickRandomCards(
          cachedProducts,
          this.randomCardCount
        );

        this.renderHeroCards();
        this.startMotion();
        this.refreshProductPoolLater();
        return;
      }

      try {
        const firstPage = await firstPagePromise;

        if (
          !this.isDestroyed &&
          firstPage &&
          firstPage.products.length
        ) {
          this.cards = this.pickRandomCards(
            firstPage.products,
            this.randomCardCount
          );

          this.renderHeroCards();
          this.startMotion();

          this.buildFullProductCacheLater(firstPage);
          return;
        }
      } catch (error) {
        console.warn(
          '[Cardcomplex Hero] Produkte konnten nicht geladen werden.',
          error
        );
      }

      if (this.isDestroyed) return;

      this.renderHeroCards();
      this.startMotion();
    }

    readNumber(value, fallback) {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    getCacheKey() {
      return 'cn-home-storefront-products-full-v1';
    }

    readProductCache() {
      const keys = [
        this.getCacheKey(),
        'cn-home-storefront-products-v8-full-pool-live'
      ];

      let bestProducts = [];
      let bestSavedAt = 0;

      keys.forEach((key) => {
        try {
          const raw = window.localStorage.getItem(key);
          if (!raw) return;

          const cached = JSON.parse(raw);
          if (!cached || !Array.isArray(cached.products)) return;

          const products = cached.products
            .filter((product) => product?.key && product?.src)
            .map((product, index) => ({
              ...product,
              index,
              srcSmall: product.srcSmall || product.src,
              srcMedium: product.srcMedium || product.src,
              srcLarge: product.srcLarge || product.src
            }));

          if (
            products.length > bestProducts.length ||
            (
              products.length === bestProducts.length &&
              cached.savedAt > bestSavedAt
            )
          ) {
            bestProducts = products;
            bestSavedAt = cached.savedAt || 0;
          }
        } catch (_error) {}
      });

      if (bestProducts.length) {
        this.writeProductCache(bestProducts, bestSavedAt || Date.now());
      }

      return bestProducts;
    }

    writeProductCache(products, savedAt = Date.now()) {
      try {
        window.localStorage.setItem(
          this.getCacheKey(),
          JSON.stringify({ savedAt, products })
        );
      } catch (_error) {}
    }

    isCacheFresh() {
      try {
        const raw = window.localStorage.getItem(this.getCacheKey());
        if (!raw) return false;

        const cached = JSON.parse(raw);
        if (!Number.isFinite(cached.savedAt)) return false;

        const maxAge = this.productCacheHours * 60 * 60 * 1000;
        return Date.now() - cached.savedAt <= maxAge;
      } catch (_error) {
        return false;
      }
    }

    runAfterCriticalLoad(callback) {
      const run = () => {
        if (this.isDestroyed) return;

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(callback, { timeout: 5000 });
        } else {
          window.setTimeout(callback, 1800);
        }
      };

      if (document.readyState === 'complete') {
        run();
      } else {
        window.addEventListener('load', run, { once: true });
      }
    }

    refreshProductPoolLater() {
      if (this.isCacheFresh()) return;

      this.runAfterCriticalLoad(async () => {
        try {
          const products = await this.fetchAllProducts();
          if (!products.length) return;

          this.writeProductCache(products);

          /*
            Sichtbare Karten werden bewusst nicht ersetzt.
            Der neue A-bis-Z-Pool greift erst beim nächsten Karten-Recycling
            und verhindert dadurch eine zweite, langsame Bild-Ladewelle.
          */
          this.cards = this.pickRandomCards(
            products,
            this.randomCardCount
          );
          this.cardCursor = 0;
        } catch (error) {
          console.warn(
            '[Cardcomplex Hero] Produktcache konnte nicht aktualisiert werden.',
            error
          );
        }
      });
    }

    buildFullProductCacheLater(firstPage) {
      this.runAfterCriticalLoad(async () => {
        try {
          const products = await this.fetchRemainingProducts(firstPage);
          if (!products.length) return;

          this.writeProductCache(products);

          /*
            Der schnelle erste Render bleibt unverändert.
            Neu einlaufende Karten stammen danach aus dem gesamten Sortiment.
          */
          this.cards = this.pickRandomCards(
            products,
            this.randomCardCount
          );
          this.cardCursor = 0;
        } catch (error) {
          console.warn(
            '[Cardcomplex Hero] Vollständiger Produktpool konnte nicht geladen werden.',
            error
          );
        }
      });
    }

    getStorefrontQuery() {
      return `
        query CardcomplexHeroProducts($cursor: String) {
          products(first: 250, after: $cursor, sortKey: TITLE) {
            nodes {
              id
              title
              featuredImage {
                small: url(transform: {
                  maxWidth: 96
                  maxHeight: 140
                  preferredContentType: WEBP
                })
                medium: url(transform: {
                  maxWidth: 144
                  maxHeight: 210
                  preferredContentType: WEBP
                })
                large: url(transform: {
                  maxWidth: 192
                  maxHeight: 280
                  preferredContentType: WEBP
                })
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      `;
    }

    async fetchProductPage(cursor) {
      if (!this.storefrontToken) {
        throw new Error('Öffentlicher Storefront-Token fehlt.');
      }

      const response = await fetch(this.storefrontEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        priority: cursor ? 'low' : 'high',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken
        },
        body: JSON.stringify({
          query: this.getStorefrontQuery(),
          variables: { cursor }
        })
      });

      if (!response.ok) {
        throw new Error(`Storefront API HTTP ${response.status}`);
      }

      const payload = await response.json();

      if (payload.errors?.length) {
        throw new Error(
          payload.errors.map((error) => error.message).join(' | ')
        );
      }

      const connection = payload?.data?.products;

      if (!connection) {
        return {
          products: [],
          hasNextPage: false,
          endCursor: null
        };
      }

      const products = connection.nodes
        .map((product, index) => {
          const smallSrc = product?.featuredImage?.small || '';
          const mediumSrc = product?.featuredImage?.medium || smallSrc;
          const largeSrc = product?.featuredImage?.large || mediumSrc;

          if (!mediumSrc) return null;

          return {
            index,
            key: product.id || mediumSrc,
            src: mediumSrc,
            srcSmall: smallSrc,
            srcMedium: mediumSrc,
            srcLarge: largeSrc,
            label: product.title || `Karte ${index + 1}`,
            laneName: index % 2 === 0 ? 'top' : 'bottom'
          };
        })
        .filter(Boolean);

      return {
        products,
        hasNextPage: Boolean(connection.pageInfo?.hasNextPage),
        endCursor: connection.pageInfo?.endCursor || null
      };
    }

    async fetchRemainingProducts(firstPage) {
      const products = [];
      const seen = new Set();

      const appendProducts = (items) => {
        items.forEach((product) => {
          if (!product?.key || seen.has(product.key)) return;
          seen.add(product.key);
          products.push(product);
        });
      };

      appendProducts(firstPage.products);

      let cursor = firstPage.endCursor;
      let hasNextPage = firstPage.hasNextPage;

      while (hasNextPage && cursor) {
        const page = await this.fetchProductPage(cursor);
        appendProducts(page.products);
        cursor = page.endCursor;
        hasNextPage = page.hasNextPage;
      }

      return products;
    }

    async fetchAllProducts() {
      const firstPage = await this.fetchProductPage(null);
      return this.fetchRemainingProducts(firstPage);
    }

    pickRandomCards(products, amount) {
      const shuffled = [...products];

      for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [shuffled[index], shuffled[randomIndex]] = [
          shuffled[randomIndex],
          shuffled[index]
        ];
      }

      return shuffled
        .slice(0, Math.min(amount, shuffled.length))
        .map((card, index) => ({
          ...card,
          index,
          laneName: index % 2 === 0 ? 'top' : 'bottom'
        }));
    }

    readFallbackCards() {
      return Array.from(
        this.section.querySelectorAll('[data-cn-home-card-source]')
      ).map((source, index) => ({
        index,
        key: source.dataset.cardKey || `card-${index}`,
        src: source.dataset.cardSrc || '',
        label: source.dataset.cardLabel || `Karte ${index + 1}`,
        laneName: index % 2 === 0 ? 'top' : 'bottom'
      }));
    }

    readWords() {
      const words = Array.from(
        this.section.querySelectorAll('[data-cn-home-word-source]')
      )
        .map((node) => (node.textContent || '').trim())
        .filter(Boolean);

      return Array.from(new Set(words));
    }

    measure() {
      const panelRect = this.panel.getBoundingClientRect();
      const style = window.getComputedStyle(this.panel);
      const cssSize = this.readNumber(
        style.getPropertyValue('--cn-home-card-size'),
        95
      );

      this.isMobileMode = this.mobileQuery
        ? this.mobileQuery.matches
        : window.innerWidth <= 749;

      this.isPhraseMode = this.phraseQuery
        ? this.phraseQuery.matches
        : window.innerWidth <= 989;

      this.duration = this.isMobileMode
        ? this.mobileDuration
        : this.desktopDuration;

      this.gapPx = this.isMobileMode
        ? this.mobileGapPx
        : this.desktopGapPx;

      this.panelWidth = Math.max(1, panelRect.width);
      this.cardWidth = Math.max(1, cssSize);
      this.spacing = this.cardWidth + this.gapPx;
      this.edgePeek = Math.min(Math.round(this.cardWidth * 0.34), 34);
      this.exitBuffer = Math.max(28, this.edgePeek + this.gapPx);

      const travel =
        this.panelWidth +
        this.cardWidth +
        this.exitBuffer * 2;

      this.speed = travel / Math.max(6, this.duration);
    }

    getCardsPerLane() {
      return Math.ceil(
        (this.panelWidth + this.cardWidth + this.exitBuffer * 2) /
        this.spacing
      ) + 1;
    }

    getActiveCardCount() {
      return Math.min(
        this.cards.length,
        this.getCardsPerLane() * 2
      );
    }

    renderHeroCards() {
      this.topLane.replaceChildren();
      this.bottomLane.replaceChildren();
      this.items = [];

      const activeCards = this.cards.slice(
        0,
        this.getActiveCardCount()
      );

      const fragmentTop = document.createDocumentFragment();
      const fragmentBottom = document.createDocumentFragment();

      activeCards.forEach((card, index) => {
        const element = this.createCardElement(card, index);
        const item = {
          el: element,
          card,
          laneName: card.laneName,
          direction: card.laneName === 'top' ? -1 : 1,
          x: 0
        };

        this.items.push(item);

        if (card.laneName === 'top') {
          fragmentTop.appendChild(element);
        } else {
          fragmentBottom.appendChild(element);
        }
      });

      this.topLane.appendChild(fragmentTop);
      this.bottomLane.appendChild(fragmentBottom);

      this.cardCursor =
        activeCards.length % Math.max(1, this.cards.length);

      this.arrangeLane('top');
      this.arrangeLane('bottom');
      this.items.forEach((item) => this.applyItemPosition(item));
    }

    createCardElement(card, visibleIndex) {
      const item = document.createElement('div');
      item.className = 'cn-home-flying-card';
      item.dataset.cnHomeGeneratedCard = 'true';

      this.updateCardElement(item, card, {
        initial: true,
        visibleIndex
      });

      return item;
    }

    updateCardElement(
      item,
      card,
      { initial = false, visibleIndex = 0 } = {}
    ) {
      item.replaceChildren();

      if (!card.src) {
        const placeholder = document.createElement('span');
        placeholder.className = 'cn-home-flying-card__placeholder';
        placeholder.innerHTML =
          `<span></span><strong>${this.escapeHtml(card.label)}</strong><small>Bild wählen</small>`;
        item.appendChild(placeholder);
        return;
      }

      const image = document.createElement('img');

      /*
        WICHTIG:
        Priorität, Loading-Modus, Decoding, Größen und srcset werden
        gesetzt, BEVOR src gesetzt wird. Dadurch startet der Browser
        den Request direkt mit der korrekten Hero-Priorität und muss
        nicht erst einen normalen Request hochstufen.
      */
      image.loading = 'eager';
      image.fetchPriority = initial ? 'high' : 'low';
      image.setAttribute(
        'fetchpriority',
        initial ? 'high' : 'low'
      );

      image.decoding =
        initial && visibleIndex < 8
          ? 'sync'
          : 'async';

      image.width = Math.round(this.cardWidth);
      image.height = Math.round(this.cardWidth * 86 / 59);
      image.alt = '';
      image.sizes = `${Math.round(this.cardWidth)}px`;

      const srcset = [
        card.srcSmall ? `${card.srcSmall} 96w` : '',
        card.srcMedium ? `${card.srcMedium} 144w` : '',
        card.srcLarge ? `${card.srcLarge} 192w` : ''
      ].filter(Boolean).join(', ');

      if (srcset) {
        image.srcset = srcset;
      }

      /* src bewusst zuletzt setzen */
      image.src = card.srcMedium || card.src;

      item.appendChild(image);
    }

    getNextCard() {
      if (!this.cards.length) return null;

      const card =
        this.cards[this.cardCursor % this.cards.length];

      this.cardCursor =
        (this.cardCursor + 1) % this.cards.length;

      return card;
    }

    arrangeLane(laneName) {
      const laneItems = this.items.filter(
        (item) => item.laneName === laneName
      );

      if (!laneItems.length) return;

      if (laneName === 'top') {
        const startX = -this.edgePeek;

        laneItems.forEach((item, index) => {
          item.direction = -1;
          item.x = startX + index * this.spacing;
        });
      } else {
        const startX =
          this.panelWidth -
          this.cardWidth +
          this.edgePeek;

        laneItems.forEach((item, index) => {
          item.direction = 1;
          item.x = startX - index * this.spacing;
        });
      }
    }

    startMotion() {
      if (
        !this.items.length ||
        this.rafId ||
        !this.isDocumentVisible ||
        !this.isInViewport
      ) {
        return;
      }

      if (
        window.matchMedia?.(
          '(prefers-reduced-motion: reduce)'
        ).matches
      ) {
        return;
      }

      this.lastFrame = performance.now();
      this.rafId = window.requestAnimationFrame(
        (time) => this.tick(time)
      );
    }

    pauseMotion() {
      if (!this.rafId) return;
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    tick(time) {
      if (
        this.isDestroyed ||
        !this.isDocumentVisible ||
        !this.isInViewport
      ) {
        this.rafId = null;
        return;
      }

      const delta = Math.min(
        0.05,
        Math.max(0, (time - this.lastFrame) / 1000)
      );

      this.lastFrame = time;

      this.items.forEach((item) => {
        item.x += item.direction * this.speed * delta;
      });

      this.recycleExitedItems('top');
      this.recycleExitedItems('bottom');

      this.items.forEach((item) => {
        this.applyItemPosition(item);
      });

      this.rafId = window.requestAnimationFrame(
        (nextTime) => this.tick(nextTime)
      );
    }

    recycleExitedItems(laneName) {
      this.items
        .filter((item) => item.laneName === laneName)
        .forEach((item) => {
          const exitedTop =
            laneName === 'top' &&
            item.x < -this.cardWidth - this.exitBuffer;

          const exitedBottom =
            laneName === 'bottom' &&
            item.x > this.panelWidth + this.exitBuffer;

          if (exitedTop) {
            this.moveToOppositeLane(item, 'bottom');
          } else if (exitedBottom) {
            this.moveToOppositeLane(item, 'top');
          }
        });
    }

    moveToOppositeLane(item, newLaneName) {
      const nextCard = this.getNextCard();

      if (nextCard) {
        item.card = {
          ...nextCard,
          laneName: newLaneName
        };

        this.updateCardElement(item.el, item.card);
      }

      item.laneName = newLaneName;
      item.direction = newLaneName === 'top' ? -1 : 1;
      this.getLane(newLaneName).appendChild(item.el);

      item.x = newLaneName === 'top'
        ? this.getTopEntryX()
        : this.getBottomEntryX();
    }

    getTopEntryX() {
      const positions = this.items
        .filter((item) => item.laneName === 'top')
        .map((item) => item.x);

      const maxX = positions.length
        ? Math.max(...positions)
        : this.panelWidth + this.exitBuffer;

      return Math.max(
        this.panelWidth + this.exitBuffer,
        maxX + this.spacing
      );
    }

    getBottomEntryX() {
      const positions = this.items
        .filter((item) => item.laneName === 'bottom')
        .map((item) => item.x);

      const minX = positions.length
        ? Math.min(...positions)
        : -this.cardWidth - this.exitBuffer;

      return Math.min(
        -this.cardWidth - this.exitBuffer,
        minX - this.spacing
      );
    }

    getLane(laneName) {
      return laneName === 'top'
        ? this.topLane
        : this.bottomLane;
    }

    applyItemPosition(item) {
      item.el.style.setProperty(
        '--cn-card-x',
        `${item.x}px`
      );
    }

    startWords() {
      if (!this.wordElement || this.words.length < 2) return;

      let index = Math.max(
        0,
        this.words.indexOf(
          (this.wordElement.textContent || '').trim()
        )
      );

      this.wordIntervalTimer = window.setInterval(() => {
        if (
          this.isDestroyed ||
          this.isWordAnimating ||
          !this.isDocumentVisible ||
          !this.isInViewport
        ) {
          return;
        }

        const nextIndex =
          (index + 1) % this.words.length;

        const nextWord = this.words[nextIndex];

        if (
          this.isPhraseMode &&
          this.deckLine &&
          this.deckPhrase
        ) {
          if (this.animatePhrase(nextWord)) {
            index = nextIndex;
          }
          return;
        }

        if (this.animateDesktopWord(nextWord)) {
          index = nextIndex;
        }
      }, this.wordInterval);
    }

    animateDesktopWord(nextWord) {
      if (
        !this.wordElement ||
        !nextWord ||
        this.isWordAnimating
      ) {
        return false;
      }

      this.isWordAnimating = true;
      this.pendingWord = nextWord;
      this.wordElement.classList.add('is-changing');

      window.clearTimeout(this.desktopWordTimer);

      this.desktopWordTimer = window.setTimeout(() => {
        if (this.isDestroyed) return;

        this.wordElement.textContent =
          this.pendingWord || nextWord;

        this.pendingWord = '';

        window.requestAnimationFrame(() => {
          if (this.isDestroyed) return;

          this.wordElement.classList.remove('is-changing');
          this.isWordAnimating = false;
          this.desktopWordTimer = null;
        });
      }, 230);

      return true;
    }

    animatePhrase(nextWord) {
      if (
        !this.deckPhrase ||
        !this.wordElement ||
        !nextWord ||
        this.isWordAnimating
      ) {
        return false;
      }

      this.isWordAnimating = true;
      this.pendingWord = nextWord;

      this.deckPhrase.classList.remove('is-preparing');
      this.deckPhrase.classList.add('is-leaving');

      window.clearTimeout(this.phraseOutTimer);

      this.phraseOutTimer = window.setTimeout(() => {
        if (this.isDestroyed) return;

        this.wordElement.textContent =
          this.pendingWord || nextWord;

        this.pendingWord = '';

        this.deckPhrase.classList.remove('is-leaving');
        this.deckPhrase.classList.add('is-preparing');

        this.deckPhrase.getBoundingClientRect();

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (this.isDestroyed) return;

            this.deckPhrase.classList.remove('is-preparing');

            window.clearTimeout(this.phraseInTimer);

            this.phraseInTimer = window.setTimeout(() => {
              if (this.isDestroyed) return;

              this.deckPhrase.classList.remove(
                'is-leaving',
                'is-preparing'
              );

              this.isWordAnimating = false;
              this.phraseOutTimer = null;
              this.phraseInTimer = null;
            }, 380);
          });
        });
      }, 260);

      return true;
    }

    finishPhraseAnimation(commitPendingWord = true) {
      window.clearTimeout(this.phraseOutTimer);
      window.clearTimeout(this.phraseInTimer);
      window.clearTimeout(this.desktopWordTimer);

      this.phraseOutTimer = null;
      this.phraseInTimer = null;
      this.desktopWordTimer = null;

      if (
        commitPendingWord &&
        this.pendingWord &&
        this.wordElement
      ) {
        this.wordElement.textContent = this.pendingWord;
      }

      this.pendingWord = '';

      this.deckPhrase?.classList.remove(
        'is-leaving',
        'is-preparing'
      );

      this.wordElement?.classList.remove('is-changing');
      this.isWordAnimating = false;
    }

    bindLifecycle() {
      this.visibilityBound = () => {
        this.isDocumentVisible = !document.hidden;

        if (this.isDocumentVisible) {
          this.startMotion();
        } else {
          this.pauseMotion();
          this.finishPhraseAnimation(true);
        }
      };

      document.addEventListener(
        'visibilitychange',
        this.visibilityBound
      );

      if ('IntersectionObserver' in window) {
        this.intersectionObserver =
          new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (!entry || this.isDestroyed) return;

            this.isInViewport =
              entry.isIntersecting ||
              entry.intersectionRatio > 0;

            if (this.isInViewport) {
              this.startMotion();
            } else {
              this.pauseMotion();
              this.finishPhraseAnimation(true);
            }
          }, {
            rootMargin: '220px 0px',
            threshold: 0
          });

        this.intersectionObserver.observe(this.panel);
      }
    }

    bindResize() {
      this.resizeBound = () => this.handleResize();

      window.addEventListener(
        'resize',
        this.resizeBound,
        { passive: true }
      );

      if ('ResizeObserver' in window) {
        this.resizeObserver =
          new ResizeObserver(() => this.handleResize());

        this.resizeObserver.observe(this.panel);
      }

      this.mediaChangeBound = () => this.handleResize();

      this.mobileQuery?.addEventListener?.(
        'change',
        this.mediaChangeBound
      );

      this.phraseMediaChangeBound =
        () => this.handleResize();

      this.phraseQuery?.addEventListener?.(
        'change',
        this.phraseMediaChangeBound
      );
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        if (this.isDestroyed) return;

        const previousMode = this.isMobileMode;
        const previousWidth = this.panelWidth;
        const previousSpacing = this.spacing;

        this.measure();

        const modeChanged =
          previousMode !== this.isMobileMode;

        const layoutChanged =
          Math.abs(previousWidth - this.panelWidth) > 2 ||
          Math.abs(previousSpacing - this.spacing) > 1;

        if (modeChanged) {
          this.renderHeroCards();
          return;
        }

        if (layoutChanged) {
          this.arrangeLane('top');
          this.arrangeLane('bottom');
          this.items.forEach((item) => {
            this.applyItemPosition(item);
          });
        }
      }, 120);
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    destroy() {
      this.isDestroyed = true;
      this.pauseMotion();

      window.clearInterval(this.wordIntervalTimer);
      window.clearTimeout(this.resizeTimer);
      this.finishPhraseAnimation(false);

      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();

      if (this.visibilityBound) {
        document.removeEventListener(
          'visibilitychange',
          this.visibilityBound
        );
      }

      if (this.resizeBound) {
        window.removeEventListener(
          'resize',
          this.resizeBound
        );
      }

      this.mobileQuery?.removeEventListener?.(
        'change',
        this.mediaChangeBound
      );

      this.phraseQuery?.removeEventListener?.(
        'change',
        this.phraseMediaChangeBound
      );

      this.items.forEach((item) => item.el.remove());
      this.items = [];
    }
  }

  class CardcomplexThemePreview {
    constructor(preview) {
      this.preview = preview;
      this.viewport = preview.querySelector('[data-cn-home-theme-viewport]');
      this.track = preview.querySelector('[data-cn-home-theme-track]');
      this.rafId = null;
      this.lastFrame = 0;
      this.cycleHeight = 0;
      this.position = 0;
      this.isPaused = false;
      this.isDestroyed = false;
      this.resizeObserver = null;
      this.resizeTimer = null;
      this.resumeTimer = null;
      this.fadeStateRafId = null;
      this.horizontalPosition = 0;
      this.horizontalCycleWidth = 0;
      this.isTouching = false;
      this.isAutoScrolling = false;
      this.mobileResumeTimer = null;
      this.desktopQuery = window.matchMedia
        ? window.matchMedia('(min-width: 750px)')
        : null;
      this.reducedMotionQuery = window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
      this.speed = Math.max(8, this.readNumber(preview.dataset.cnHomeThemeSpeed, 26));

      if (!this.viewport || !this.track) return;

      this.removeDuplicates();

      if (this.track.dataset.cnThemeShuffled !== 'true') {
        this.shuffleOriginalItems();
        this.track.dataset.cnThemeShuffled = 'true';
      }

      this.items = Array.from(this.track.children).filter((item) => !item.dataset.cnThemeDuplicate);

      if (this.items.length < 2) return;

      this.cloneItems();
      this.bindEvents();

      window.requestAnimationFrame(() => {
        if (this.isDestroyed) return;
        this.measure(true);
        this.start();

        window.setTimeout(() => {
          if (this.isDestroyed) return;
          this.measure(false);
        }, 250);
      });
    }

    readNumber(value, fallback) {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    isDesktop() {
      return this.desktopQuery ? this.desktopQuery.matches : window.innerWidth >= 750;
    }

    prefersReducedMotion() {
      return this.reducedMotionQuery ? this.reducedMotionQuery.matches : false;
    }

    removeDuplicates() {
      this.track.querySelectorAll('[data-cn-theme-duplicate]').forEach((node) => node.remove());
    }

    shuffleOriginalItems() {
      const items = Array.from(this.track.children).filter(
        (item) => !item.dataset.cnThemeDuplicate
      );

      for (let index = items.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [items[index], items[randomIndex]] = [items[randomIndex], items[index]];
      }

      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.appendChild(item));
      this.track.appendChild(fragment);
    }

    cloneItems() {
      this.items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.dataset.cnThemeDuplicate = 'true';
        clone.setAttribute('aria-hidden', 'true');

        if (clone.matches('a')) {
          clone.setAttribute('tabindex', '-1');
        }

        clone.querySelectorAll('a, button, input, select, textarea').forEach((focusable) => {
          focusable.setAttribute('tabindex', '-1');
        });

        this.track.appendChild(clone);
      });
    }

    bindEvents() {
      this.pauseBound = () => this.setPaused(true);
      this.resumeBound = () => this.setPaused(false);
      this.mobileInteractionStartBound = () => this.startMobileInteraction();
      this.mobileInteractionEndBound = () => this.endMobileInteraction();
      this.measureBound = () => this.scheduleMeasure();
      this.syncManualScrollBound = () => {
        if (!this.isAutoScrolling && (this.isPaused || this.isTouching)) {
          this.syncPositionFromScroll();
        }

        this.scheduleHorizontalFadeState();

        if (!this.isDesktop() && this.isPaused && !this.isTouching) {
          this.scheduleMobileResume(480);
        }
      };
      this.pauseTemporarilyBound = () => this.pauseTemporarily();

      this.preview.addEventListener('pointerenter', this.pauseBound);
      this.preview.addEventListener('pointerleave', this.resumeBound);
      this.preview.addEventListener('focusin', this.pauseBound);
      this.preview.addEventListener('focusout', this.resumeBound);

      this.viewport.addEventListener('scroll', this.syncManualScrollBound, { passive: true });
      this.viewport.addEventListener('pointerdown', this.mobileInteractionStartBound);
      this.viewport.addEventListener('pointerup', this.mobileInteractionEndBound);
      this.viewport.addEventListener('pointercancel', this.mobileInteractionEndBound);
      this.viewport.addEventListener('wheel', this.pauseTemporarilyBound, { passive: true });

      window.addEventListener('resize', this.measureBound, { passive: true });
      window.addEventListener('load', this.measureBound, { once: true });

      if (this.desktopQuery) {
        if (this.desktopQuery.addEventListener) {
          this.desktopQuery.addEventListener('change', this.measureBound);
        } else if (this.desktopQuery.addListener) {
          this.desktopQuery.addListener(this.measureBound);
        }
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        this.resizeObserver.observe(this.track);
        this.resizeObserver.observe(this.viewport);
      }

      this.track.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', this.measureBound, { once: true });
        img.addEventListener('error', this.measureBound, { once: true });
      });
    }

    setPaused(isPaused) {
      window.clearTimeout(this.resumeTimer);
      this.isPaused = isPaused;
      this.preview.classList.toggle('is-paused', isPaused);

      if (isPaused) {
        this.syncPositionFromScroll();
      }
    }

    startMobileInteraction() {
      this.isTouching = true;
      window.clearTimeout(this.mobileResumeTimer);
      this.setPaused(true);
      this.syncPositionFromScroll();
    }

    endMobileInteraction() {
      this.isTouching = false;
      this.syncPositionFromScroll();
      this.scheduleMobileResume(520);
    }

    scheduleMobileResume(delay = 520) {
      window.clearTimeout(this.mobileResumeTimer);

      this.mobileResumeTimer = window.setTimeout(() => {
        if (this.isDestroyed || this.isTouching) return;

        this.syncPositionFromScroll();
        this.setPaused(false);
      }, delay);
    }

    pauseTemporarily() {
      this.setPaused(true);
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => {
        if (this.isDestroyed) return;
        if (this.preview.matches(':hover') || this.preview.contains(document.activeElement)) return;
        this.setPaused(false);
      }, 1400);
    }

    syncPositionFromScroll() {
      if (this.isDesktop()) {
        if (this.cycleHeight > 1) {
          this.position = this.viewport.scrollTop % this.cycleHeight;
        } else {
          this.position = this.viewport.scrollTop;
        }
        return;
      }

      if (this.horizontalCycleWidth > 1) {
        this.horizontalPosition =
          Math.max(0, this.viewport.scrollLeft) % this.horizontalCycleWidth;
      } else {
        this.horizontalPosition = Math.max(0, this.viewport.scrollLeft);
      }
    }

    scheduleHorizontalFadeState() {
      if (this.fadeStateRafId) return;

      this.fadeStateRafId = window.requestAnimationFrame(() => {
        this.fadeStateRafId = null;
        if (this.isDestroyed) return;
        this.updateHorizontalFadeState();
      });
    }

    updateHorizontalFadeState() {
      const maxScrollLeft = Math.max(
        0,
        this.viewport.scrollWidth - this.viewport.clientWidth
      );

      const threshold = 2;
      const rawScrollLeft = this.viewport.scrollLeft;

      const currentScrollLeft = Math.min(
        maxScrollLeft,
        Math.max(0, rawScrollLeft)
      );

      const hasHorizontalOverflow = maxScrollLeft > threshold;
      const canScrollLeft = hasHorizontalOverflow && currentScrollLeft > threshold;
      const canScrollRight = hasHorizontalOverflow && currentScrollLeft < (maxScrollLeft - threshold);

      this.preview.dataset.canScrollLeft = String(canScrollLeft);
      this.preview.dataset.canScrollRight = String(canScrollRight);
    }

    scheduleMeasure() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        if (this.isDestroyed) return;
        this.measure(false);
      }, 100);
    }

    updatePreviewHeight() {
      if (!this.isDesktop()) {
        this.preview.style.removeProperty('--cn-home-theme-preview-height');
        return;
      }

      const panel = this.preview.closest('.cn-home-theme-intro__panel');
      const content = panel ? panel.querySelector('.cn-home-theme-intro__content') : null;

      if (!content) return;

      const contentHeight = Math.ceil(content.getBoundingClientRect().height);
      const nextHeight = Math.max(300, Math.min(430, contentHeight));

      this.preview.style.setProperty('--cn-home-theme-preview-height', `${nextHeight}px`);
    }

    getOriginalCycleHeight() {
      const firstDuplicate = this.track.querySelector('[data-cn-theme-duplicate]');

      if (firstDuplicate && firstDuplicate.offsetTop > 0) {
        return firstDuplicate.offsetTop;
      }

      const styles = window.getComputedStyle(this.track);
      const rowGap = this.readNumber(styles.rowGap || styles.gap, 10);
      const originalHeight = this.items.reduce((sum, item) => sum + item.offsetHeight, 0);
      const gapsHeight = Math.max(0, this.items.length - 1) * rowGap;

      return Math.max(1, originalHeight + gapsHeight);
    }

    getOriginalCycleWidth() {
      const firstItem = this.items[0];
      const firstDuplicate = this.track.querySelector('[data-cn-theme-duplicate]');

      if (firstItem && firstDuplicate) {
        const width = firstDuplicate.offsetLeft - firstItem.offsetLeft;
        if (width > 1) return width;
      }

      const styles = window.getComputedStyle(this.track);
      const columnGap = this.readNumber(styles.columnGap || styles.gap, 10);
      const originalWidth = this.items.reduce((sum, item) => sum + item.offsetWidth, 0);
      const gapsWidth = Math.max(0, this.items.length) * columnGap;

      return Math.max(1, originalWidth + gapsWidth);
    }

    measure(resetPosition) {
      this.updatePreviewHeight();

      const previousCycleHeight = this.cycleHeight;
      this.cycleHeight = this.getOriginalCycleHeight();
      this.horizontalCycleWidth = this.getOriginalCycleWidth();

      this.scheduleHorizontalFadeState();

      if (!this.isDesktop()) {
        this.position = 0;
        this.viewport.scrollTop = 0;

        if (resetPosition) {
          this.horizontalPosition =
            Math.max(0, this.viewport.scrollLeft) % this.horizontalCycleWidth;
        } else if (this.horizontalCycleWidth > 1) {
          this.horizontalPosition =
            Math.max(0, this.viewport.scrollLeft) % this.horizontalCycleWidth;
        }

        if (this.viewport.scrollLeft >= this.horizontalCycleWidth) {
          this.viewport.scrollLeft =
            this.viewport.scrollLeft % this.horizontalCycleWidth;
        }

        return;
      }

      if (resetPosition) {
        this.position = this.viewport.scrollTop % this.cycleHeight;
      } else if (previousCycleHeight > 1) {
        this.position = this.viewport.scrollTop % this.cycleHeight;
      }

      if (this.viewport.scrollTop >= this.cycleHeight) {
        this.viewport.scrollTop = this.viewport.scrollTop % this.cycleHeight;
      }
    }

    start() {
      if (this.prefersReducedMotion() || this.rafId) return;

      this.lastFrame = performance.now();
      this.rafId = window.requestAnimationFrame((time) => this.tick(time));
    }

    tick(time) {
      if (this.isDestroyed) return;

      const delta = Math.min(0.05, Math.max(0, (time - this.lastFrame) / 1000));
      this.lastFrame = time;

      if (!this.isPaused) {
        if (this.isDesktop() && this.cycleHeight > 1) {
          this.position += this.speed * delta;

          while (this.position >= this.cycleHeight) {
            this.position -= this.cycleHeight;
          }

          this.viewport.scrollTop = this.position;
        } else if (!this.isDesktop() && this.horizontalCycleWidth > 1) {
          this.horizontalPosition += this.speed * delta;

          while (this.horizontalPosition >= this.horizontalCycleWidth) {
            this.horizontalPosition -= this.horizontalCycleWidth;
          }

          this.isAutoScrolling = true;
          this.viewport.scrollLeft = this.horizontalPosition;
          this.isAutoScrolling = false;

          this.scheduleHorizontalFadeState();
        }
      }

      this.rafId = window.requestAnimationFrame((nextTime) => this.tick(nextTime));
    }

    destroy() {
      this.isDestroyed = true;
      this.isAutoScrolling = false;

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      window.clearTimeout(this.resizeTimer);
      window.clearTimeout(this.resumeTimer);
      window.clearTimeout(this.mobileResumeTimer);

      if (this.fadeStateRafId) {
        window.cancelAnimationFrame(this.fadeStateRafId);
        this.fadeStateRafId = null;
      }

      this.preview.removeAttribute('data-can-scroll-left');
      this.preview.removeAttribute('data-can-scroll-right');

      if (this.pauseBound) {
        this.preview.removeEventListener('pointerenter', this.pauseBound);
        this.preview.removeEventListener('focusin', this.pauseBound);
      }

      if (this.mobileInteractionStartBound) {
        this.viewport.removeEventListener('pointerdown', this.mobileInteractionStartBound);
      }

      if (this.mobileInteractionEndBound) {
        this.viewport.removeEventListener('pointerup', this.mobileInteractionEndBound);
        this.viewport.removeEventListener('pointercancel', this.mobileInteractionEndBound);
      }

      if (this.pauseTemporarilyBound) {
        this.viewport.removeEventListener('wheel', this.pauseTemporarilyBound);
      }

      if (this.resumeBound) {
        this.preview.removeEventListener('pointerleave', this.resumeBound);
        this.preview.removeEventListener('focusout', this.resumeBound);
      }

      if (this.syncManualScrollBound) {
        this.viewport.removeEventListener('scroll', this.syncManualScrollBound);
      }

      if (this.measureBound) {
        window.removeEventListener('resize', this.measureBound);

        if (this.desktopQuery) {
          if (this.desktopQuery.removeEventListener) {
            this.desktopQuery.removeEventListener('change', this.measureBound);
          } else if (this.desktopQuery.removeListener) {
            this.desktopQuery.removeListener(this.measureBound);
          }
        }
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      this.removeDuplicates();
      this.preview.classList.remove('is-paused');
      this.preview.style.removeProperty('--cn-home-theme-preview-height');
    }
  }

  function setupThemePreview(root) {
    const scope = root || document;

    scope.querySelectorAll('[data-cn-home-theme-preview]').forEach((preview) => {
      const previous = themePreviewControllers.get(preview);
      if (previous) previous.destroy();

      const controller = new CardcomplexThemePreview(preview);
      themePreviewControllers.set(preview, controller);
    });
  }

  function init(root) {
    const scope = root || document;

    scope.querySelectorAll('[data-cn-home-video-hero]').forEach((section) => {
      const previous = controllers.get(section);
      if (previous) previous.destroy();

      const controller = new CardcomplexVideoHero(section);
      controllers.set(section, controller);
    });

    setupThemePreview(scope);
  }

  /*
    Das Script wird direkt nach der Homepage-Hero-Section eingebunden.
    Sobald der Hero im DOM existiert, wird deshalb sofort initialisiert
    und nicht mehr bis DOMContentLoaded auf den Rest der Seite gewartet.
  */
  if (document.querySelector('[data-cn-home-video-hero]')) {
    init(document);
  } else if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(document),
      { once: true }
    );
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:select', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-cn-home-video-hero]').forEach((section) => {
      const controller = controllers.get(section);
      if (controller) controller.destroy();
      controllers.delete(section);
    });

    event.target.querySelectorAll('[data-cn-home-theme-preview]').forEach((preview) => {
      const controller = themePreviewControllers.get(preview);
      if (controller) controller.destroy();
      themePreviewControllers.delete(preview);
    });
  });
})();
